import { useMemo, useState } from 'react';
import { canCreateNodeInAccessMode } from '../../../core/policies/access-gate/preview-node-entitlement';
import { useGraphStore } from '../../graph-view/services/graph-store';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import { useDeidentificationSettingsStore } from '../../preferences/services/deidentification-settings-store';
import { useAIAssistWorkspaceStore } from '../services/ai-workspace-store';

type WorkspacePanelKey = 'raw' | 'masked' | 'prompt';
type WorkspaceNodeTypeFilter = 'All' | 'Human' | 'Project' | 'Occupation';
type WorkspaceSubpageKey = 'selected' | 'create';

export function AIAssistWorkspace({ open, onClose }: { open: boolean; onClose?: () => void }) {
  const graph = useGraphStore((state) => state.graph);
  const readonlyMode = useGraphStore((state) => state.readonlyMode);
  const accessMode = useGraphStore((state) => state.accessMode);
  const createNodeExamplePayload = useDeidentificationSettingsStore((state) => state.config.createNodePromptContract.examplePayload);
  const { t } = useLocale();
  const {
    selectedNodeIds,
    userPromptText,
    sourceJson,
    maskedJson,
    promptPreview,
    responseDraft,
    createNodeUserPromptText,
    createNodePromptPreview,
    createNodeResponseDraft,
    operationsApplyIssues,
    createNodeApplyIssues,
    stale,
    toggleNodeSelection,
    setUserPromptText,
    buildSourceBundle,
    buildMaskedBundle,
    buildPromptPreview,
    setResponseDraft,
    setCreateNodeUserPromptText,
    setCreateNodeResponseDraft,
    applyResponseDraft,
    applyCreateNodeResponseDraft,
  } = useAIAssistWorkspaceStore();
  const [copyStatusKey, setCopyStatusKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [nodeTypeFilter, setNodeTypeFilter] = useState<WorkspaceNodeTypeFilter>('All');
  const [expandedPanelKey, setExpandedPanelKey] = useState<WorkspacePanelKey | null>(null);
  const [activeSubpage, setActiveSubpage] = useState<WorkspaceSubpageKey>('selected');

  const selectedLabel = useMemo(
    () => selectedNodeIds.map((nodeId) => graph.nodes.find((node) => node.nodeId === nodeId)?.displayName ?? nodeId).join(', '),
    [graph.nodes, selectedNodeIds]
  );
  const visibleNodes = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();

    return graph.nodes.filter((node) => {
      if (nodeTypeFilter !== 'All' && node.type !== nodeTypeFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [node.displayName, node.nodeId, ...node.tags]
        .join(' ')
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [graph.nodes, nodeTypeFilter, searchQuery]);
  const expandedPanel = useMemo(() => {
    if (!expandedPanelKey) {
      return null;
    }

    if (expandedPanelKey === 'raw') {
      return {
        key: 'raw' as const,
        label: t('workspace.aiAssist.rawJson'),
        value: sourceJson,
      };
    }

    if (expandedPanelKey === 'masked') {
      return {
        key: 'masked' as const,
        label: t('workspace.aiAssist.maskedJson'),
        value: maskedJson,
      };
    }

    return {
      key: 'prompt' as const,
      label: t('workspace.aiAssist.promptPreview'),
      value: promptPreview.renderedPrompt,
    };
  }, [expandedPanelKey, maskedJson, promptPreview.renderedPrompt, sourceJson, t]);
  const createNodeExampleJson = useMemo(
    () => JSON.stringify(createNodeExamplePayload, null, 2),
    [createNodeExamplePayload]
  );
  const canImportOperations = !readonlyMode && responseDraft.validationStatus === 'valid' && !!responseDraft.parsedEnvelope;
  const canCreateNode = !readonlyMode
    && canCreateNodeInAccessMode(accessMode, graph.nodes.length)
    && createNodeResponseDraft.validationStatus === 'valid'
    && !!createNodeResponseDraft.parsedPayload;

  if (!open) {
    return null;
  }

  async function copyText(text: string, statusKey: string) {
    const nextText = text.trim();
    if (!nextText) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(nextText);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = nextText;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopyStatusKey(statusKey);
  }

  function copyButtonLabel(statusKey: string, defaultLabel: string) {
    return copyStatusKey === statusKey ? t('workspace.aiAssist.copiedShort') : defaultLabel;
  }

  return (
    <section aria-label={t('workspace.aiAssist.title')} className="floating-panel workspace-panel workspace-panel--assistant">
      <header className="workspace-header">
        <div className="workspace-title-group">
          {onClose && <button className="hud-subtle-button" onClick={onClose} type="button">{t('close')}</button>}
          <h2>{t('workspace.aiAssist.title')}</h2>
          <p className="workspace-subtitle">{t('workspace.aiAssist.description')}</p>
          {readonlyMode && <p className="notice-text" role="note">{t('workspace.aiAssist.readonly')}</p>}
          {stale && <p className="alert-text" role="alert">{t('workspace.aiAssist.stale')}</p>}
        </div>
      </header>
      <div aria-label={t('workspace.aiAssist.pageNav')} className="workspace-subpage-nav" role="tablist">
        <button
          aria-label={t('workspace.aiAssist.selectedNodePage')}
          aria-controls="ai-workspace-selected-panel"
          aria-selected={activeSubpage === 'selected'}
          className="workspace-subpage-button"
          id="ai-workspace-selected-tab"
          onClick={() => setActiveSubpage('selected')}
          role="tab"
          type="button"
        >
          <span aria-hidden="true" className="workspace-subpage-index">01</span>
          <span className="workspace-subpage-copy">
            <span className="workspace-subpage-title">{t('workspace.aiAssist.selectedNodePage')}</span>
            <span className="workspace-subpage-description">{t('workspace.aiAssist.selectedNodePageHint')}</span>
          </span>
        </button>
        <button
          aria-label={t('workspace.aiAssist.createNodePage')}
          aria-controls="ai-workspace-create-panel"
          aria-selected={activeSubpage === 'create'}
          className="workspace-subpage-button"
          id="ai-workspace-create-tab"
          onClick={() => setActiveSubpage('create')}
          role="tab"
          type="button"
        >
          <span aria-hidden="true" className="workspace-subpage-index">02</span>
          <span className="workspace-subpage-copy">
            <span className="workspace-subpage-title">{t('workspace.aiAssist.createNodePage')}</span>
            <span className="workspace-subpage-description">{t('workspace.aiAssist.createNodePageHint')}</span>
          </span>
        </button>
      </div>
      {activeSubpage === 'selected' ? (
        <div aria-labelledby="ai-workspace-selected-tab" className="ai-workspace-layout" id="ai-workspace-selected-panel" role="tabpanel">
          <div className="ai-sidebar">
            <section className="panel-card ai-selection-panel">
              <div className="workspace-title-group">
                <h3>{t('workspace.aiAssist.selectedNodes')}</h3>
                <p className="notice-text">{selectedLabel || t('workspace.aiAssist.noneSelected')}</p>
                <p className="notice-text">{t('workspace.aiAssist.selectionHint')}</p>
              </div>
              <div className="form-grid form-grid--2">
                <label className="field-stack">
                  <span className="field-label">{t('workspace.aiAssist.searchNodes')}</span>
                  <input
                    aria-label={t('workspace.aiAssist.searchNodes')}
                    className="workspace-input"
                    onChange={(event) => setSearchQuery(event.currentTarget.value)}
                    type="search"
                    value={searchQuery}
                  />
                </label>
                <label className="field-stack">
                  <span className="field-label">{t('workspace.aiAssist.nodeType')}</span>
                  <select
                    aria-label={t('workspace.aiAssist.nodeType')}
                    className="workspace-input"
                    onChange={(event) => setNodeTypeFilter(event.currentTarget.value as WorkspaceNodeTypeFilter)}
                    value={nodeTypeFilter}
                  >
                    <option value="All">{t('workspace.aiAssist.filterAll')}</option>
                    <option value="Human">{t('human')}</option>
                    <option value="Project">{t('project')}</option>
                    <option value="Occupation">{t('occupation')}</option>
                  </select>
                </label>
              </div>
              <ul className="ai-selection-list">
                {visibleNodes.map((node) => (
                  <li key={node.nodeId}>
                    <label className="ai-selection-item">
                      <input checked={selectedNodeIds.includes(node.nodeId)} onChange={() => toggleNodeSelection(node.nodeId)} type="checkbox" />
                      <span className="field-stack">
                        <span className="ai-selection-name">{node.displayName}</span>
                        <span className="ai-selection-id">{node.nodeId}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {visibleNodes.length === 0 && <p className="notice-text">{t('workspace.aiAssist.noMatchingNodes')}</p>}
              <div className="ai-toolbar">
                <button className="hud-chip" onClick={() => buildSourceBundle(graph)} type="button">{t('workspace.aiAssist.buildSource')}</button>
                <button className="hud-chip" onClick={() => buildMaskedBundle()} type="button">{t('workspace.aiAssist.buildMasked')}</button>
                <button className="hud-chip" onClick={() => buildPromptPreview()} type="button">{t('workspace.aiAssist.buildPrompt')}</button>
              </div>
            </section>
          </div>
          <div className="ai-main">
            <div className="ai-data-grid">
              <section className="panel-card field-stack">
                <div className="field-header">
                  <span className="field-label">{t('workspace.aiAssist.rawJson')}</span>
                  <div className="hud-toolbar">
                    <button className={`hud-subtle-button${copyStatusKey === 'raw' ? ' is-copied' : ''}`} onClick={() => void copyText(sourceJson, 'raw')} type="button">{copyButtonLabel('raw', t('workspace.aiAssist.copyRawJson'))}</button>
                    <button className="hud-subtle-button" onClick={() => setExpandedPanelKey('raw')} type="button">{t('workspace.aiAssist.expandRawJson')}</button>
                  </div>
                </div>
                <textarea aria-label={t('workspace.aiAssist.rawJson')} className="workspace-textarea workspace-textarea--json" readOnly rows={12} value={sourceJson} />
              </section>
              <section className="panel-card field-stack">
                <div className="field-header">
                  <span className="field-label">{t('workspace.aiAssist.maskedJson')}</span>
                  <div className="hud-toolbar">
                    <button className={`hud-subtle-button${copyStatusKey === 'masked' ? ' is-copied' : ''}`} onClick={() => void copyText(maskedJson, 'masked')} type="button">{copyButtonLabel('masked', t('workspace.aiAssist.copyMaskedJson'))}</button>
                    <button className="hud-subtle-button" onClick={() => setExpandedPanelKey('masked')} type="button">{t('workspace.aiAssist.expandMaskedJson')}</button>
                  </div>
                </div>
                <textarea aria-label={t('workspace.aiAssist.maskedJson')} className="workspace-textarea workspace-textarea--json" readOnly rows={12} value={maskedJson} />
              </section>
            </div>
            <div className="ai-prompt-grid">
              <label className="panel-card field-stack">
                <div className="field-header">
                  <span className="field-label">{t('workspace.aiAssist.userPrompt')}</span>
                  {copyStatusKey ? <span className="copy-status">{t('workspace.aiAssist.copied')}</span> : null}
                </div>
                <textarea
                  aria-label={t('workspace.aiAssist.userPrompt')}
                  className="workspace-textarea"
                  onChange={(event) => setUserPromptText(event.currentTarget.value)}
                  placeholder={t('workspace.aiAssist.userPromptPlaceholder')}
                  rows={4}
                  value={userPromptText}
                />
              </label>
              <section className="panel-card field-stack">
                <div className="field-header">
                  <span className="field-label">{t('workspace.aiAssist.promptPreview')}</span>
                  <div className="hud-toolbar">
                    <button className={`hud-subtle-button${copyStatusKey === 'prompt' ? ' is-copied' : ''}`} onClick={() => void copyText(promptPreview.renderedPrompt, 'prompt')} type="button">{copyButtonLabel('prompt', t('workspace.aiAssist.copyPromptPreview'))}</button>
                    <button className="hud-subtle-button" onClick={() => setExpandedPanelKey('prompt')} type="button">{t('workspace.aiAssist.expandPromptPreview')}</button>
                  </div>
                </div>
                <textarea aria-label={t('workspace.aiAssist.promptPreview')} className="workspace-textarea workspace-textarea--prompt" readOnly rows={14} value={promptPreview.renderedPrompt} />
              </section>
            </div>
            <div className="ai-data-grid">
              <label className="panel-card field-stack">
                <span className="field-label">{t('workspace.aiAssist.responseDraft')}</span>
                <textarea aria-label={t('workspace.aiAssist.responseDraft')} className="workspace-textarea workspace-textarea--response" onChange={(event) => setResponseDraft(event.currentTarget.value)} rows={14} value={responseDraft.rawText} />
              </label>
              <section className="panel-card panel-stack">
                <h3>{t('workspace.aiAssist.importOperations')}</h3>
                <button className="hud-chip" disabled={!canImportOperations} onClick={() => applyResponseDraft()} type="button">{t('workspace.aiAssist.importOperations')}</button>
                {responseDraft.validationStatus !== 'empty' && (
                  <p className={responseDraft.validationStatus === 'valid' ? 'notice-text' : 'alert-text'} role={responseDraft.validationStatus === 'valid' ? 'status' : 'alert'}>
                    {responseDraft.validationStatus === 'valid'
                      ? t('workspace.aiAssist.responseValid')
                      : responseDraft.issues.join(' ')}
                  </p>
                )}
                {operationsApplyIssues.length > 0 && (
                  <ul className="status-list" role="alert">
                    {operationsApplyIssues.map((issue) => <li key={issue}>{issue}</li>)}
                  </ul>
                )}
                <h3>{t('workspace.aiAssist.diff')}</h3>
                <ul className="diff-list">
                  {promptPreview.diffEntries.map((entry) => <li key={entry.diffId}>{entry.beforeText} → {entry.afterText}</li>)}
                </ul>
                <div className="panel-stack">
                  <p className="notice-text">{t('workspace.aiAssist.systemPrompt')}</p>
                  <p className="notice-text">{t('workspace.aiAssist.exampleResponse')}</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : (
        <div aria-labelledby="ai-workspace-create-tab" className="ai-create-layout" id="ai-workspace-create-panel" role="tabpanel">
          <section className="panel-card ai-create-panel">
            <div className="workspace-title-group">
              <h3>{t('workspace.aiAssist.createNodeSection')}</h3>
              <p className="notice-text">{t('workspace.aiAssist.createNodeHint')}</p>
              {accessMode === 'preview' && !canCreateNodeInAccessMode(accessMode, graph.nodes.length) && (
                <p className="notice-text" role="note">{t('previewNodeLimitReached')}</p>
              )}
            </div>
            <label className="field-stack">
              <span className="field-label">{t('workspace.aiAssist.createNodeRequest')}</span>
              <textarea
                aria-label={t('workspace.aiAssist.createNodeRequest')}
                className="workspace-textarea"
                onChange={(event) => setCreateNodeUserPromptText(event.currentTarget.value)}
                placeholder={t('workspace.aiAssist.createNodeRequestPlaceholder')}
                rows={4}
                value={createNodeUserPromptText}
              />
            </label>
            <section className="field-stack">
              <div className="field-header">
                <span className="field-label">{t('workspace.aiAssist.createNodePromptPreview')}</span>
                <button className={`hud-subtle-button${copyStatusKey === 'create-prompt' ? ' is-copied' : ''}`} onClick={() => void copyText(createNodePromptPreview, 'create-prompt')} type="button">{copyButtonLabel('create-prompt', t('workspace.aiAssist.copyCreateNodePrompt'))}</button>
              </div>
              <textarea
                aria-label={t('workspace.aiAssist.createNodePromptPreview')}
                className="workspace-textarea workspace-textarea--prompt"
                readOnly
                rows={18}
                value={createNodePromptPreview}
              />
            </section>
          </section>
          <div className="ai-main">
            <section className="panel-card field-stack">
              <span className="field-label">{t('workspace.aiAssist.createNodeExample')}</span>
              <textarea
                aria-label={t('workspace.aiAssist.createNodeExample')}
                className="workspace-textarea workspace-textarea--json"
                readOnly
                rows={12}
                value={createNodeExampleJson}
              />
            </section>
            <section className="panel-card field-stack">
              <span className="field-label">{t('workspace.aiAssist.createNodeJson')}</span>
              <textarea
                aria-label={t('workspace.aiAssist.createNodeJson')}
                className="workspace-textarea workspace-textarea--response"
                onChange={(event) => setCreateNodeResponseDraft(event.currentTarget.value)}
                rows={14}
                value={createNodeResponseDraft.rawText}
              />
              <section className="panel-stack">
                <h4>{t('workspace.aiAssist.importActions')}</h4>
                <button className="hud-chip" disabled={!canCreateNode} onClick={() => applyCreateNodeResponseDraft()} type="button">{t('workspace.aiAssist.createNodeAction')}</button>
                {createNodeResponseDraft.validationStatus !== 'empty' && (
                  <p className={createNodeResponseDraft.validationStatus === 'valid' ? 'notice-text' : 'alert-text'} role={createNodeResponseDraft.validationStatus === 'valid' ? 'status' : 'alert'}>
                    {createNodeResponseDraft.validationStatus === 'valid'
                      ? t('workspace.aiAssist.createNodeResponseValid')
                      : createNodeResponseDraft.issues.join(' ')}
                  </p>
                )}
                {createNodeApplyIssues.length > 0 && (
                  <ul className="status-list" role="alert">
                    {createNodeApplyIssues.map((issue) => <li key={issue}>{issue}</li>)}
                  </ul>
                )}
              </section>
            </section>
          </div>
        </div>
      )}
      {expandedPanel && (
        <div className="workspace-editor-backdrop">
          <section aria-label={expandedPanel.label} className="floating-panel workspace-editor-dialog" role="dialog">
            <div className="workspace-header">
              <div className="workspace-title-group">
                <h3>{expandedPanel.label}</h3>
                {copyStatusKey === expandedPanel.key ? <p className="notice-text">{t('workspace.aiAssist.copied')}</p> : null}
              </div>
              <button className="hud-subtle-button" onClick={() => setExpandedPanelKey(null)} type="button">{t('close')}</button>
            </div>
            <textarea
              aria-label={expandedPanel.label}
              className="workspace-textarea workspace-textarea--json workspace-textarea--fullscreen"
              readOnly
              rows={20}
              value={expandedPanel.value}
            />
          </section>
        </div>
      )}
    </section>
  );
}
